import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import { defaultLanguage, languageCodes } from "./config"

// All UI translations (~500 KB raw, ~100 KB gzip) bundled statically.
// This is intentional: ensures correct hydration, no flash of EN content,
// correct SEO, and works with gatsby develop + gatsby build.
// Page translations (Ghost content, ~1.8 MB) are NOT bundled here -
// they are loaded only during SSR via gatsby-ssr.js.
import enCommon from "../locales/en/common.json"
import enHome from "../locales/en/home.json"
import enPricing from "../locales/en/pricing.json"
import enEnterprise from "../locales/en/enterprise.json"

import esCommon from "../locales/es/common.json"
import esHome from "../locales/es/home.json"
import esPricing from "../locales/es/pricing.json"
import esEnterprise from "../locales/es/enterprise.json"

import frCommon from "../locales/fr/common.json"
import frHome from "../locales/fr/home.json"
import frPricing from "../locales/fr/pricing.json"
import frEnterprise from "../locales/fr/enterprise.json"

import deCommon from "../locales/de/common.json"
import deHome from "../locales/de/home.json"
import dePricing from "../locales/de/pricing.json"
import deEnterprise from "../locales/de/enterprise.json"

import itCommon from "../locales/it/common.json"
import itHome from "../locales/it/home.json"
import itPricing from "../locales/it/pricing.json"
import itEnterprise from "../locales/it/enterprise.json"

import ptCommon from "../locales/pt/common.json"
import ptHome from "../locales/pt/home.json"
import ptPricing from "../locales/pt/pricing.json"
import ptEnterprise from "../locales/pt/enterprise.json"

import brCommon from "../locales/br/common.json"
import brHome from "../locales/br/home.json"
import brPricing from "../locales/br/pricing.json"
import brEnterprise from "../locales/br/enterprise.json"

import uaCommon from "../locales/ua/common.json"
import uaHome from "../locales/ua/home.json"
import uaPricing from "../locales/ua/pricing.json"
import uaEnterprise from "../locales/ua/enterprise.json"

import byCommon from "../locales/by/common.json"
import byHome from "../locales/by/home.json"
import byPricing from "../locales/by/pricing.json"
import byEnterprise from "../locales/by/enterprise.json"

import mtCommon from "../locales/mt/common.json"
import mtHome from "../locales/mt/home.json"
import mtPricing from "../locales/mt/pricing.json"
import mtEnterprise from "../locales/mt/enterprise.json"

import thCommon from "../locales/th/common.json"
import thHome from "../locales/th/home.json"
import thPricing from "../locales/th/pricing.json"
import thEnterprise from "../locales/th/enterprise.json"

const resources = {
  en: { common: enCommon, home: enHome, pricing: enPricing, enterprise: enEnterprise },
  es: { common: esCommon, home: esHome, pricing: esPricing, enterprise: esEnterprise },
  fr: { common: frCommon, home: frHome, pricing: frPricing, enterprise: frEnterprise },
  de: { common: deCommon, home: deHome, pricing: dePricing, enterprise: deEnterprise },
  it: { common: itCommon, home: itHome, pricing: itPricing, enterprise: itEnterprise },
  pt: { common: ptCommon, home: ptHome, pricing: ptPricing, enterprise: ptEnterprise },
  br: { common: brCommon, home: brHome, pricing: brPricing, enterprise: brEnterprise },
  ua: { common: uaCommon, home: uaHome, pricing: uaPricing, enterprise: uaEnterprise },
  by: { common: byCommon, home: byHome, pricing: byPricing, enterprise: byEnterprise },
  mt: { common: mtCommon, home: mtHome, pricing: mtPricing, enterprise: mtEnterprise },
  th: { common: thCommon, home: thHome, pricing: thPricing, enterprise: thEnterprise },
}

const isBrowser = typeof window !== "undefined"

/**
 * The language a page was rendered in on the server, read from its URL: a supported code
 * as the first path segment (`/es/pricing/`), English otherwise (`/pricing/`, `/blog/...`).
 *
 * @param {string} pathname - `window.location.pathname`.
 * @returns {string} A code from `languageCodes`.
 */
export const languageOfPath = pathname => {
  const firstSegment = pathname.split("/").filter(Boolean)[0]

  return languageCodes.includes(firstSegment) ? firstSegment : defaultLanguage
}

// The browser must start in the language the server rendered, or the first render does not
// match the HTML it is attaching to. This used to run the language detector with the order
// path -> cookie -> navigator: on an English page the path names no language, so a visitor
// with a Spanish browser (or a stored non-English choice) hydrated the English HTML in
// Spanish. React threw about 14 hydration errors per page load and drew the page again from
// scratch - 9,500 such errors a month from some 500 people, none of them on a
// language-prefixed URL. Measured on the live home page: 0 errors with an English browser,
// 14 with a Spanish one.
//
// The browser's language and the stored choice still apply, AFTER the first render:
// useLanguageRedirect sends a first-time visitor to their language, and Layout sends a
// returning one to the language they picked.
i18n.use(initReactI18next).init({
  resources,
  lng: isBrowser ? languageOfPath(window.location.pathname) : defaultLanguage,
  fallbackLng: defaultLanguage,
  supportedLngs: languageCodes,
  defaultNS: "common",
  ns: ["common", "home", "pricing", "enterprise"],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
})

export default i18n
